import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineOppositeContent,
    TimelineSeparator,
    timelineOppositeContentClasses,
} from '@mui/lab';
import { Box, Card, Dialog, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
    CoachSession,
    CoachingMetadataMetadata,
    CoachingMetadataMetadataActionEnum,
    CustomerUserDetails,
} from '../../../backendsdk';
import { TrackedIconButton as IconButton } from '../../../components/TrackedComponents';
import useProfile, { ExtendedProfile } from '../../../hooks/profile';
import { formatPhoneNumber } from '../../../utils/Str';

export const getMetadataText = (
    metadata: CoachingMetadataMetadata,
    details: CoachSession,
    profile: ExtendedProfile,
    users: CustomerUserDetails[],
    t: CallableFunction,
    timestamp?: number,
) => {
    let metadataText = '';
    const { action, manager_id, phone_number, status, method } = metadata;
    const datetime = !!timestamp ? dayjs.unix(timestamp).format(profile.dateTimeFormat) : undefined;
    const prefix = !!timestamp ? 'content.coach.details.metadata.most_recent' : 'content.coach.details.metadata';
    const managerName = users.find((user) => user.user_id === manager_id)?.name;
    const formatText = (key: string, additionalProps = {}) =>
        t(key, { datetime, manager: managerName, ...additionalProps });
    if (action === CoachingMetadataMetadataActionEnum.Generate) {
        metadataText = formatText(`${prefix}.generate`);
    } else if (action === CoachingMetadataMetadataActionEnum.Create) {
        const createKey = details.auto_generated ? `${prefix}.save` : `${prefix}.create`;
        metadataText = formatText(createKey);
    } else if (action === CoachingMetadataMetadataActionEnum.Send) {
        const sendKey = phone_number ? `${prefix}.send_with_phone` : `${prefix}.send`;
        const additionalProps: { phone?: string; status?: string } = {};
        if (phone_number) {
            additionalProps['phone'] = formatPhoneNumber(profile.customer.settings.country, phone_number, true);
        }
        if (status) {
            additionalProps['status'] = t(`content.coach.details.metadata.status.${status}`);
        }
        metadataText = formatText(sendKey, additionalProps);
    } else if (action === CoachingMetadataMetadataActionEnum.Open) {
        metadataText = formatText(`${prefix}.open`);
    } else if (action === CoachingMetadataMetadataActionEnum.Reopen) {
        metadataText = formatText(`${prefix}.reopen`);
    } else if (action === CoachingMetadataMetadataActionEnum.Watch) {
        const numVideosToWatch = details.events.length;
        const numVideosWatched = (metadata.coach_event_ids || []).length;
        if (numVideosWatched === numVideosToWatch) {
            metadataText =
                numVideosToWatch === 1 ? formatText(`${prefix}.watch_one`) : formatText(`${prefix}.watch_all`);
        } else {
            metadataText = formatText(`${prefix}.watch_some`, { count: numVideosWatched, total: numVideosToWatch });
        }
    } else if (action === CoachingMetadataMetadataActionEnum.Complete) {
        const completeKey = method === 'remote' ? `${prefix}.complete_remote` : `${prefix}.complete_in_office`;
        metadataText = formatText(completeKey);
    }
    return metadataText;
};

interface MetadataModalProps {
    open: boolean;
    details: CoachSession;
    users: CustomerUserDetails[];
    onClose: CallableFunction;
}

const MetadataModal: React.FC<MetadataModalProps> = (props: MetadataModalProps) => {
    const { profile } = useProfile();
    const { t } = useTranslation();

    return (
        <Dialog
            id="effectiveness-dialog"
            data-testid="effectiveness-dialog"
            open={props.open}
            onClose={() => props.onClose()}
            sx={{
                '& .MuiDialog-container': {
                    '& .MuiPaper-root': {
                        width: '100%',
                        maxWidth: '600px',
                    },
                },
            }}
        >
            <Box
                sx={{
                    backgroundColor: 'secondary.main',
                    position: 'sticky',
                    top: 0,
                    p: 1,
                    zIndex: 2,
                }}
            >
                <Box sx={{ position: 'absolute', display: 'flex', right: 4, top: 4 }}>
                    <IconButton id="btn-close-modal" size="small" onClick={() => props.onClose()}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '24px' }}>
                    <HistoryIcon sx={{ mr: 0.5 }} />
                    <Typography>{t('content.coach.details.metadata.header')}</Typography>
                </Box>
            </Box>
            <Box sx={{ p: 1 }}>
                <Card>
                    <Timeline
                        sx={{
                            [`& .${timelineOppositeContentClasses.root}`]: {
                                flex: profile.locale === 'il' ? 0.35 : 0.4,
                                paddingLeft: 0,
                            },
                            p: 1,
                            pt: 2,
                            m: 0,
                        }}
                    >
                        {props.details.metadata.map((metadata, index) => {
                            const metadataText = getMetadataText(
                                metadata.metadata,
                                props.details,
                                profile,
                                props.users,
                                t,
                            );
                            const isLast = index === props.details.metadata.length - 1;
                            return (
                                <TimelineItem key={index} sx={{ minHeight: isLast ? '50px !important' : undefined }}>
                                    <TimelineOppositeContent color="textSecondary" fontSize={14} sx={{ pt: 1 }}>
                                        {dayjs.unix(metadata.timestamp).format(profile.dateTimeFormat)}
                                    </TimelineOppositeContent>
                                    <TimelineSeparator>
                                        <TimelineDot color={isLast ? 'secondary' : undefined} />
                                        {!isLast && <TimelineConnector />}
                                    </TimelineSeparator>
                                    <TimelineContent>{metadataText}</TimelineContent>
                                </TimelineItem>
                            );
                        })}
                    </Timeline>
                </Card>
            </Box>
        </Dialog>
    );
};

export default MetadataModal;
